import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Inbox, LogOut, Lock, Loader2 } from "lucide-react";
import { API } from "@/lib/scroll";

const TOKEN_KEY = "ark_admin_token";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export default function AdminPage() {
  const location = useLocation();
  const stateUser = location.state?.user;
  const [authed, setAuthed] = useState(!!stateUser);
  const [checking, setChecking] = useState(!stateUser);
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [loading, setLoading] = useState(false);

  const fetchEnquiries = useCallback(async (headers) => {
    const r = await axios.get(`${API}/enquiries`, { headers, withCredentials: true });
    setData(r.data);
    setAuthed(true);
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const t = localStorage.getItem(TOKEN_KEY);
        if (t) await fetchEnquiries({ Authorization: `Bearer ${t}` });
        else await fetchEnquiries();
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      } finally {
        setChecking(false);
      }
    };
    init();
  }, [fetchEnquiries]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, r.data.access_token);
      await fetchEnquiries({ Authorization: `Bearer ${r.data.access_token}` });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setData(null);
  };

  const inputCls =
    "w-full bg-ark-800/70 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50 transition-all duration-300";

  return (
    <div className="min-h-screen bg-ark-950 text-slate-200 px-6 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <Link
            data-testid="admin-back-home-link"
            to="/"
            className="inline-flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200"
          >
            <ArrowLeft size={15} /> Back to site
          </Link>
          {authed && (
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"
            >
              <LogOut size={15} /> Logout
            </button>
          )}
        </div>

        {checking ? (
          <div className="mt-24 flex justify-center">
            <Loader2 size={28} className="animate-spin text-cyan-400" />
          </div>
        ) : !authed ? (
          <div className="mt-16 max-w-sm mx-auto">
            <div className="p-8 rounded-3xl border border-white/10 bg-ark-800/40">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
                  <Lock size={16} className="text-cyan-300" />
                </span>
                <h1 className="font-display text-xl font-bold text-white">Admin Login</h1>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Restricted area — authorised ARK IT ENTERPRISES staff only.
              </p>
              <form data-testid="admin-login-form" onSubmit={login} className="mt-6 space-y-4">
                <input
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Admin email"
                  className={inputCls}
                />
                <input
                  data-testid="admin-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className={inputCls}
                />
                {error && (
                  <p data-testid="admin-login-error" className="text-xs text-red-400">
                    {error}
                  </p>
                )}
                <button
                  data-testid="admin-login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-display font-semibold text-sm text-ark-950 bg-gradient-to-r from-slate-100 via-slate-300 to-slate-400 hover:shadow-[0_0_25px_rgba(56,189,248,0.35)] disabled:opacity-60 transition-all duration-300"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
                  {loading ? "Verifying…" : "Sign In"}
                </button>
              </form>
              <div className="mt-5 flex items-center gap-3">
                <span className="flex-1 h-px bg-white/10" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-slate-600 uppercase">or</span>
                <span className="flex-1 h-px bg-white/10" />
              </div>
              <button
                data-testid="admin-google-login-btn"
                onClick={googleLogin}
                className="mt-5 w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-full font-display font-semibold text-sm text-slate-200 border border-white/15 bg-ark-900/60 hover:border-cyan-400/50 hover:bg-ark-700/60 transition-all duration-300"
              >
                <GoogleIcon />
                Sign in with Google
              </button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="mt-8 font-display text-3xl font-bold text-white">Enquiries Inbox</h1>
            <p className="mt-2 text-sm text-slate-500">
              {data ? `${data.count} enquiry(ies) received` : "Loading…"}
            </p>
            <div data-testid="admin-enquiry-list" className="mt-8 space-y-4">
              {data && data.enquiries.length === 0 && (
                <div className="flex items-center gap-3 p-6 rounded-xl border border-white/10 text-slate-500 text-sm">
                  <Inbox size={16} /> No enquiries yet.
                </div>
              )}
              {data &&
                data.enquiries.map((e, i) => (
                  <div key={i} className="p-5 rounded-xl border border-white/10 bg-ark-800/50">
                    <div className="flex flex-wrap justify-between gap-2">
                      <p className="font-display font-semibold text-white">{e.name}</p>
                      <p className="font-mono text-[11px] text-slate-500">
                        {e.created_at ? new Date(e.created_at).toLocaleString() : ""}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-cyan-300 font-mono">{e.category}</p>
                    <p className="mt-2 text-sm text-slate-400">{e.message}</p>
                    <p className="mt-3 text-xs text-slate-500">
                      {e.email} · {e.phone} {e.company ? `· ${e.company}` : ""}
                    </p>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
